import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Play, Plus, Pencil, Trash2 } from "lucide-react";
import api, { timeAgo, apiError } from "@/lib/api";
import { Source } from "@/lib/types";
import { PageHeader, Table, StatusBadge } from "@/admin/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EMPTY = { name: "", source_type: "rss", base_url: "", polling_frequency_minutes: 30,
  publish_mode: "hybrid", confidence_threshold: 0.4, ai_enrich: true, active: true, api_key: "" };

export default function Sources() {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Source | null>(null);
  const [form, setForm] = useState<any>(EMPTY);

  const { data: sources } = useQuery({
    queryKey: ["admin-sources"],
    queryFn: async () => (await api.get("/admin/ingestion/sources")).data,
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["admin-sources"] });

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (s: Source) => {
    setEditing(s);
    setForm({ ...s, api_key: s.auth_config?.api_key || "" });
    setOpen(true);
  };

  const save = async () => {
    const payload: any = {
      name: form.name, source_type: form.source_type, base_url: form.base_url,
      polling_frequency_minutes: Number(form.polling_frequency_minutes),
      publish_mode: form.publish_mode, confidence_threshold: Number(form.confidence_threshold),
      ai_enrich: form.ai_enrich, active: form.active,
      auth_config: form.api_key ? { api_key: form.api_key } : {},
    };
    try {
      if (editing) await api.patch(`/admin/ingestion/sources/${editing.id}`, payload);
      else await api.post("/admin/ingestion/sources", payload);
      toast.success(editing ? "Source updated" : "Source created");
      setOpen(false);
      refresh();
    } catch (e) { toast.error(apiError(e)); }
  };

  const runNow = async (s: Source) => {
    try {
      await api.post(`/admin/ingestion/sources/${s.id}/run`);
      toast.success(`Ingestion started for ${s.name} — check Runs in ~30s`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (s: Source) => {
    if (!window.confirm(`Delete source "${s.name}"?`)) return;
    try {
      await api.delete(`/admin/ingestion/sources/${s.id}`);
      toast.success("Source deleted");
      refresh();
    } catch (e) { toast.error(apiError(e)); }
  };

  const toggleActive = async (s: Source, active: boolean) => {
    try {
      await api.patch(`/admin/ingestion/sources/${s.id}`, { active });
      refresh();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="admin-sources-page">
      <PageHeader title="Ingestion Sources" sub="Configure the feeds and APIs powering the automation engine"
        actions={<Button onClick={openCreate} data-testid="add-source-button"
          className="bg-[#00E5FF] text-black font-bold hover:bg-[#00c9e0]"><Plus className="h-4 w-4 mr-1" /> Add source</Button>} />

      <Table headers={["Source", "Type", "Mode", "Every", "Last run", "Active", "Actions"]}>
        {(sources || []).map((s: Source) => (
          <tr key={s.id} data-testid={`source-row-${s.name.toLowerCase().replace(/ /g, "-")}`}>
            <td className="px-4 py-3">
              <p className="text-white font-medium">{s.name}</p>
              <p className="font-mono text-[10px] text-gray-600 max-w-[240px] truncate">{s.base_url}</p>
            </td>
            <td className="px-4 py-3 font-mono text-xs text-gray-400 uppercase">{s.source_type}</td>
            <td className="px-4 py-3"><StatusBadge status={s.publish_mode} /></td>
            <td className="px-4 py-3 font-mono text-xs text-gray-400">{s.polling_frequency_minutes}m</td>
            <td className="px-4 py-3">
              <p className="font-mono text-xs text-gray-400">{s.last_run_at ? timeAgo(s.last_run_at) : "never"}</p>
              {s.last_run_status && <StatusBadge status={s.last_run_status} />}
            </td>
            <td className="px-4 py-3">
              <Switch checked={s.active} onCheckedChange={(v) => toggleActive(s, v)}
                data-testid={`source-toggle-${s.id}`} />
            </td>
            <td className="px-4 py-3">
              <div className="flex gap-1">
                <Button size="icon" variant="ghost" onClick={() => runNow(s)} data-testid={`source-run-${s.id}`}
                  title="Run now" className="text-green-400 hover:text-green-300 h-8 w-8">
                  <Play className="h-4 w-4" />
                </Button>
                <Button size="icon" variant="ghost" onClick={() => openEdit(s)} data-testid={`source-edit-${s.id}`}
                  className="text-gray-400 hover:text-white h-8 w-8"><Pencil className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" onClick={() => remove(s)} data-testid={`source-delete-${s.id}`}
                  className="text-gray-500 hover:text-red-400 h-8 w-8"><Trash2 className="h-4 w-4" /></Button>
              </div>
            </td>
          </tr>
        ))}
      </Table>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit source" : "Add ingestion source"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="source-form-name" className="mt-1 bg-[#1A1A1A] border-white/10" />
            </div>
            <div>
              <Label>Type</Label>
              <Select value={form.source_type} onValueChange={(v) => setForm({ ...form, source_type: v })}>
                <SelectTrigger className="mt-1 bg-[#1A1A1A] border-white/10" data-testid="source-form-type">
                  <SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#121212] border-white/10 text-white">
                  <SelectItem value="rss">RSS feed</SelectItem>
                  <SelectItem value="cryptopanic">CryptoPanic API</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Feed / endpoint URL</Label>
              <Input value={form.base_url} onChange={(e) => setForm({ ...form, base_url: e.target.value })}
                data-testid="source-form-url" className="mt-1 bg-[#1A1A1A] border-white/10" />
            </div>
            {form.source_type === "cryptopanic" && (
              <div>
                <Label>API key</Label>
                <Input value={form.api_key} onChange={(e) => setForm({ ...form, api_key: e.target.value })}
                  data-testid="source-form-apikey" className="mt-1 bg-[#1A1A1A] border-white/10" />
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Poll every (minutes)</Label>
                <Input type="number" value={form.polling_frequency_minutes}
                  onChange={(e) => setForm({ ...form, polling_frequency_minutes: e.target.value })}
                  className="mt-1 bg-[#1A1A1A] border-white/10" />
              </div>
              <div>
                <Label>Confidence threshold</Label>
                <Input type="number" step="0.1" min="0" max="1" value={form.confidence_threshold}
                  onChange={(e) => setForm({ ...form, confidence_threshold: e.target.value })}
                  className="mt-1 bg-[#1A1A1A] border-white/10" />
              </div>
            </div>
            <div>
              <Label>Publish mode</Label>
              <Select value={form.publish_mode} onValueChange={(v) => setForm({ ...form, publish_mode: v })}>
                <SelectTrigger className="mt-1 bg-[#1A1A1A] border-white/10" data-testid="source-form-mode">
                  <SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#121212] border-white/10 text-white">
                  <SelectItem value="auto_publish">Auto publish (trusted)</SelectItem>
                  <SelectItem value="hybrid">Hybrid (confidence-based)</SelectItem>
                  <SelectItem value="review_required">Review required</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between">
              <Label>AI enrichment (summaries + takeaways)</Label>
              <Switch checked={form.ai_enrich} onCheckedChange={(v) => setForm({ ...form, ai_enrich: v })} />
            </div>
            <div className="flex items-center justify-between">
              <Label>Active</Label>
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
            </div>
            <Button onClick={save} data-testid="source-form-save"
              className="w-full bg-[#00E5FF] text-black font-bold hover:bg-[#00c9e0]">
              {editing ? "Save changes" : "Create source"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
